<template>
  <div
    v-if="introSimilarEvents.length || pending"
    ref="showcaseRef"
    class="-mx-4 px-4 overflow-x-hidden md:mx-0 md:px-0 md:overflow-x-visible my-5"
    data-test-id="similar-events-intro"
  >
    <Heading
      :level="2"
      :title="similarEventsTitle"
      h-class="text-h5"
      class="mb-3 md:mb-2 kft-heading--left"
      :use-h-tag="true"
    />

    <ProductsLoader
      v-show="pending"
      is-horizontal
      :products-count="introSimilarEvents.length || 6"
    />

    <div
      v-show="!pending && introSimilarEvents.length"
      class="event-similar-events-intro__scroll-area relative"
    >
      <button
        v-show="canScrollLeft"
        type="button"
        class="event-similar-events-intro__arrow event-similar-events-intro__arrow--left hidden md:flex"
        :aria-label="t('general.pagination.previous')"
        @click="scrollByDirection('left')"
      >
        <span
          class="event-similar-events-intro__arrow-button flex items-center justify-center"
        >
          <ChevronLeft class="mr-0.5 text-black" />
        </span>
      </button>

      <button
        v-show="canScrollRight"
        type="button"
        class="event-similar-events-intro__arrow event-similar-events-intro__arrow--right hidden md:flex"
        :aria-label="t('general.pagination.next')"
        @click="scrollByDirection('right')"
      >
        <span
          class="event-similar-events-intro__arrow-button flex items-center justify-center"
        >
          <ChevronRight class="ml-0.5 text-black" />
        </span>
      </button>

      <div
        ref="scrollContainerRef"
        v-dragscroll.x="enableDragScroll"
        class="event-similar-events-intro__cards flex flex-nowrap gap-4 py-3"
        @scroll.passive="updateScrollState"
        @dragscrollstart="onDragStart"
        @click.capture="onDragClick"
      >
        <template
          v-for="(item, index) in introSimilarEvents"
          :key="item.objectID"
        >
          <!-- Product card V2 (KFT-4152); the control branch is the rollback -->
          <ProductShowcaseCardV2
            v-if="isProductCardV2"
            class="event-similar-events-intro__card shrink-0"
            :product="item as SearchEngineEventItem"
            :is-on-wishlist="
              isSearchEngineEventItemOnWishlist(item as SearchEngineEventItem)
            "
            :open-in-new-tab="!isMobile"
            in-slider
            :before-navigation-callback="() => handleClickOnCard(item, index)"
            @click:wishlist="handleClickWishlist(item)"
          />
          <ProductCard
            v-else
            class="event-similar-events-intro__card shrink-0"
            v-bind="getProductCardProps(item, index)"
            is-slide
            :before-navigation-callback="() => handleClickOnCard(item, index)"
            @click:wishlist="handleClickWishlist(item)"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { dragscroll } from 'vue-dragscroll'
import ChevronRight from '~/assets/icons/chevron-right.svg?component'
import ChevronLeft from '~/assets/icons/chevron-left.svg?component'
import type { SearchEngineEventItem } from '~/composables/types/searchEngine'
import type { ProductCardImageBadge } from '~/components/product/ProductCardImage.vue'
import ProductShowcaseCardV2 from '~/components/product/ProductShowcaseCardV2.vue'
import { useProductCardVariant } from '~/composables/product/useProductCardVariant'
import { GtmItemListId } from '~/composables/useKftGtm/types'
import {
  sendViewItemList,
  sendSelectListItem,
} from '~/composables/useGtmEvents'
import { useAlgoliaConversion } from '~/composables/algolia/useAlgoliaConversion'
import { useSendEventToAlgolia } from '~/composables/product/useProduct'
import { useGetFormattedDuration } from '~/composables/useGetFormattedDuration'
import { useWishlistSidebarUiState } from '~/composables/useUiState'
import { useWishlist } from '~/composables/useWishlist'
import {
  useIntersectionObserver,
  IMPRESSION_OBSERVER_OPTIONS,
} from '~/composables/useIntersectionObserver'
import { searchEngineEventGetters } from '~/utils/searchEngineEventGetters'
import { convertLocaleIntoBCP47 } from '~/utils/localeHelpers'

const vDragscroll = dragscroll

const DESKTOP_VIEWPORT = '(min-width: 768px)'

const { isGiftcard } = defineProps<{
  isGiftcard: boolean
}>()

const { t, locale } = useI18n()
const { isMobile } = useDevice()
const { isV2: isProductCardV2 } = useProductCardVariant()
const { client: insightsClient, token: userToken } = useAlgoliaSearchInsights()
const { sendViewedObjectIDs } = useAlgoliaConversion()
const {
  addSearchEngineEventItem,
  isSearchEngineEventItemOnWishlist,
  removeSearchEngineEventItem,
} = useWishlist()
const { toggle: toggleWishlistSidebar } = useWishlistSidebarUiState()
const { introSimilarEvents, pending } = useEventSimilarEvents()
const similarEventsTitle = useSimilarEventsTitle(isGiftcard)

const localeBCP47 = convertLocaleIntoBCP47(locale.value)
const fnGetFormattedDuration = (duration: unknown) =>
  useGetFormattedDuration(duration)

const scrollContainerRef = ref<HTMLElement | null>(null)
const enableDragScroll = ref(false)
const canScrollLeft = ref(false)
const canScrollRight = ref(false)
const dragged = ref(false)
const dragTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

const updateScrollState = () => {
  const container = scrollContainerRef.value
  if (!container) return

  canScrollLeft.value = container.scrollLeft > 50
  canScrollRight.value =
    container.scrollLeft + container.offsetWidth < container.scrollWidth - 50
}

const scrollByDirection = (direction: 'left' | 'right') => {
  const container = scrollContainerRef.value
  if (!container) return

  const offset =
    direction === 'right' ? container.offsetWidth : -container.offsetWidth

  container.scrollTo({
    left: container.scrollLeft + offset,
    behavior: 'smooth',
  })
}

const onDragStart = () => {
  if (!enableDragScroll.value) return

  if (dragTimeout.value) {
    clearTimeout(dragTimeout.value)
  }

  dragged.value = false
  dragTimeout.value = setTimeout(() => {
    dragged.value = true
  }, 100)
}

const onDragClick = (event: Event) => {
  if (!enableDragScroll.value || !dragged.value) return

  event.preventDefault()
  dragged.value = false
}

const getBadges = (item: SearchEngineEventItem): ProductCardImageBadge[] => {
  const badges: ProductCardImageBadge[] = []
  const generalBadgeLabel = searchEngineEventGetters.getBadgeLabel(item, t)

  if (generalBadgeLabel) {
    badges.push({
      label: generalBadgeLabel,
      variant: 'default',
    })
  }

  if (searchEngineEventGetters.getHasPromotions(item)) {
    badges.push({ label: t('product.card.sale'), variant: 'promo' })
  }

  return badges
}

const getProductCardProps = (item: SearchEngineEventItem, index: number) => ({
  dataInsightsObjectId: item.objectID,
  duration: fnGetFormattedDuration(
    searchEngineEventGetters.fnGetDuration(item)
  ),
  durationTitle: t('product.card.duration'),
  minNumberOfPeople: item.min_number_of_people ?? 0,
  maxNumberOfPeople: item.max_number_of_people ?? 0,
  image: searchEngineEventGetters.getPrincipalImage(item),
  imageLoading: 'lazy' as const,
  link: searchEngineEventGetters.fnGetLink(item),
  productCategory: searchEngineEventGetters.fnGetMainCategory(item),
  productType: '',
  badges: getBadges(item),
  rating: searchEngineEventGetters.getAverageRating(item, localeBCP47),
  isPartnerRating: searchEngineEventGetters.getIsPartnerRating(item),
  isThirdPartyRating: searchEngineEventGetters.getIsThirdPartyRating(item),
  regularPrice: searchEngineEventGetters.getRegularPriceAmount(item),
  specialPrice: searchEngineEventGetters.getSpecialPriceAmount(item),
  priceSuffix: searchEngineEventGetters.getIsGroupPricing(item)
    ? t('product.card.price.groupPricingSuffix', {
        count: item.min_number_of_people,
      })
    : undefined,
  showAddToCartButton: false,
  style: { '--index': index },
  title: item.title || 'similar-events-intro',
  alt: searchEngineEventGetters.getPrincipalAlt(item),
  wishlistIcon: true,
  isOnWishlist: isSearchEngineEventItemOnWishlist(item),
  hasShippingCosts: searchEngineEventGetters.hasShippingCosts(item),
  isTeamevent: searchEngineEventGetters.getIsTeamevent(item),
  isBoxEvent: searchEngineEventGetters.isBoxEvent(item),
  openInNewTab: !isMobile,
  listProductObjectId: item.objectID,
})

const handleClickWishlist = (item: SearchEngineEventItem) => {
  if (isSearchEngineEventItemOnWishlist(item)) {
    removeSearchEngineEventItem(item)
  } else {
    addSearchEngineEventItem(item)
    toggleWishlistSidebar()
  }
}

const handleClickOnCard = (item: SearchEngineEventItem, index: number) => {
  useSendEventToAlgolia({
    objectID: item.objectID,
    position: index + 1,
    algoliaInsightsClient: insightsClient,
    label: 'Similar Events Intro - Event Page',
    queryId: '',
    locale: locale.value,
    userToken: userToken.value || '',
  })

  sendSelectListItem(GtmItemListId.EventPageSimilarClassesTop, [item], index)
}

const showcaseRef = ref<HTMLElement | null>(null)
const { intersectionHappened } = useIntersectionObserver(
  showcaseRef,
  IMPRESSION_OBSERVER_OPTIONS
)
const hasTrackedView = ref(false)

watch(
  [introSimilarEvents, intersectionHappened],
  ([products, hasIntersected]) => {
    if (
      !products.length ||
      !hasIntersected ||
      hasTrackedView.value ||
      !import.meta.client
    ) {
      return
    }

    sendViewItemList(GtmItemListId.EventPageSimilarClassesTop, products)
    sendViewedObjectIDs(products, 'Similar Events Intro - Event Page')
    hasTrackedView.value = true
  },
  { immediate: false }
)

watch(
  introSimilarEvents,
  () => {
    nextTick(() => {
      updateScrollState()
    })
  },
  { flush: 'post' }
)

onMounted(() => {
  if (!import.meta.client) return

  const mediaQuery = window.matchMedia(DESKTOP_VIEWPORT)
  const syncDragScroll = () => {
    enableDragScroll.value = mediaQuery.matches
    nextTick(() => {
      updateScrollState()
    })
  }

  syncDragScroll()
  mediaQuery.addEventListener('change', syncDragScroll)

  onBeforeUnmount(() => {
    mediaQuery.removeEventListener('change', syncDragScroll)
  })
})
</script>

<style lang="css" scoped>
@reference '~/assets/css/tailwind.css';
.event-similar-events-intro__cards {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  touch-action: pan-x pan-y;
  scroll-snap-type: x proximity;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.event-similar-events-intro__cards::-webkit-scrollbar {
  display: none;
}

.event-similar-events-intro__card {
  width: 244px;
  scroll-snap-align: start;
}

.event-similar-events-intro__card :deep(.product-card) {
  @apply flex-col;
}

.event-similar-events-intro__card :deep(.product-card__content) {
  @apply w-full px-1 pt-1.5 pl-0;
}

.event-similar-events-intro__card :deep(.card-image-wrapper) {
  @apply w-full h-[180px];
}

@media (min-width: 768px) {
  .event-similar-events-intro__cards {
    overflow-x: hidden;
    scroll-snap-type: none;
    touch-action: auto;
  }

  .event-similar-events-intro__card {
    width: 244px;
  }

  .event-similar-events-intro__card :deep(.card-image-wrapper) {
    @apply h-[222px];
  }
}

.event-similar-events-intro__arrow {
  position: absolute;
  top: 0;
  z-index: 3;
  height: 69%;
  width: 50px;
  align-items: center;
  border: 0;
  padding: 0;
  cursor: pointer;
}

.event-similar-events-intro__arrow--left {
  left: 0;
  justify-content: flex-start;
  background: linear-gradient(to right, var(--color-white), rgba(0, 0, 0, 0));
}

.event-similar-events-intro__arrow--right {
  right: 0;
  justify-content: flex-end;
  background: linear-gradient(to right, rgba(0, 0, 0, 0), var(--color-white));
}

.event-similar-events-intro__arrow-button {
  @apply w-10 h-10 rounded-full border border-gray-200 bg-white;
}
</style>
